import {
  Type,
  Code,
  TypeEdge,
  AuditEntry,
  InsertType,
  InsertCode,
  InsertTypeEdge,
  StorageData,
} from "@shared/schema";
import { nanoid } from "nanoid";
import { getCookie, getCookieAsItIS } from "./getCookie";

export type DataTypeSortOption = "Standard" | "Lex" | "Id_based";

export interface SearchResult {
  type: "TYPE" | "CODE";
  key: string;
  name: string;
  code?: string;
  path: string;
  parentPath?: string;
  score?: number;
}

type AuthSuccess = {
  id: number;
  username: string;
  email: string;
  client_id: string;
};

type LogoutSuccess = {
  success: boolean;
  message: string;
};

type RefreshSuccess = {
  success: boolean;
  message: string;
  access_token: string;
  token_type: string;
};

type AuthError = {
  detail: string;
};

export interface DataQualityIssue {
  id: string;
  type: "DUPLICATE" | "ORPHAN" | "CYCLE" | "OVERLAP";
  severity: "HIGH" | "MEDIUM" | "LOW";
  title: string;
  description: string;
  entityType: "TYPE" | "CODE";
  entityKey: string;
  path: string;
  suggestions: string[];
}

export interface ImportResult {
  success: boolean;
  processedRows: number;
  errors: Array<{
    row: number;
    message: string;
    data: any;
  }>;
  warnings: Array<{
    row: number;
    message: string;
    data: any;
  }>;
  data?: any;
}

export interface AuditLogEntry {
  timestamp: string;
  error_type: string;
  status_code: number;
  error_message: string;
  response_time_ms: number;
  [key: string]: unknown;
}

export interface AuditDebugInfo {
  audit_log_dir: string;
  audit_log_dir_absolute: string;
  audit_log_dir_exists: boolean;
  available_dates: string[];
  entries_after_filter: number;
  entries_before_filter: number;
  log_file_absolute_path: string;
  log_file_exists: boolean;
  log_file_path: string;
  target_date: string;
}

export interface AuditFilters {
  start_date: string | null;
  end_date: string | null;
  error_type: string | null;
  status_code: number | null;
}

export interface AuditData {
  date: string;
  debug: AuditDebugInfo;
  logs: AuditLogEntry[];
  filters: AuditFilters;
  limit: number;
  offset: number;
  page: number;
  total: number;
}

export interface ICodeManagerDataSource {
  // Authentication APIs
  authMe(): Promise<AuthSuccess | AuthError>;
  refresh(): Promise<RefreshSuccess | AuthError>;
  logout(): Promise<LogoutSuccess | AuthError>

  // DATAType operations
  listTypes(parentType: String, parentCode: number): Promise<Type>;
  listAllTypes(): Promise<any>;

  // DATAType operations
  listDataTypes(pageIndex: number, pageSize?: number, typeKey?: string): Promise<{ items: Type[]; total: number }>;
  listAttributes(parentCode: string): Promise<Code[]>;
  listFilterType(filterName?: string, filterType?: string): Promise<Type[]>;
  filterCatalogDataTypes(
    name?: string,
    page?: number,
    pageSize?: number,
    sort?: DataTypeSortOption
  ): Promise<{ items: Type[]; total: number; page: number; pageSize: number; page_size?: number; sort?: string }>;
  getType(key: string): Promise<Type | undefined>;
  createDataType(type: InsertType): Promise<Type>;
  updateDataType(key: number, updates: Partial<Type>): Promise<Type>;
  deleteDataType(key: string, force?: boolean): Promise<void>;

  // Code operations
  listCodes(
    typeFilter?: string,
    parentTypeFilter?: string,
    parentCodeFilter?: string
  ): Promise<Code[]>;
  getCode(type: string, code: string): Promise<Code | undefined>;
  createAttribute(Id: string, data: Partial<Code>): Promise<Code>;
  updateAttribute(type: string, code: string, updates: Partial<Code>): Promise<Code>;
  deleteAttribute(type: string, code: string, force?: boolean): Promise<void>;

  // Edge operations
  listTypeEdges(): Promise<TypeEdge[]>;
  createTypeEdge(edge: InsertTypeEdge): Promise<TypeEdge>;
  deleteTypeEdge(fromType: string, toType: string): Promise<void>;

  // Search and navigation
  search(query: string): Promise<SearchResult[]>;
  resolve(
    type: string,
    code?: string
  ): Promise<{ path: string; fullPath: string }>;

  // Data quality
  analyzeDataQuality(): Promise<any[]>;

  // Import/Export
  exportData(table_type?: "all" | "datatypes" | "attributes", format?: "csv" | "json"): Promise<Blob>;
  importData(
    file: File,
    format: "csv" | "json",
    dryRun: boolean,
    validateReferences: boolean,
    checkDuplicates: boolean,
    mode?: "insert" | "update" | "upsert",
    dataset?: "all" | "datatypes" | "attributes"
  ): Promise<ImportResult>

  // Audit
  getAuditLog(filters?: {
    errorCode?: string;
    statusCode?: string;
    dateFrom?: string;
    dateTo?: string;
    pageSize?: string;
    pageIndex?: string;
  }): Promise<AuditData[]>;

  // Stats
  getStats(): Promise<{
    typesCount: number;
    codesCount: number;
    activeTypesCount: number;
    activeCodesCount: number;
  }>;

  // AI Assistant
  chatWithAI(message: string, sessionId: string, microservice?: string, page?: number): Promise<{ assistantReply: string; sessionId: string; messages?: any[]; hasMore?: boolean; totalMessages?: number; added?: "datatype" | "attribute"; mainUrl?: { obj: string; val: string }; otherUrl?: { obj: string; val: string }[] }>;
}

export class LocalStorageDataSource implements ICodeManagerDataSource {
  private addAuditEntry(
    action: string,
    entityType: string,
    entityKey: string,
    oldValue?: any,
    newValue?: any,
    note?: string
  ): void {
    const data = localStorage.getData();
    const auditEntry: AuditEntry = {
      id: nanoid(),
      timestamp: new Date().toISOString(),
      action: action as any,
      entityType: entityType as any,
      entityKey,
      oldValue,
      newValue,
      user: "current-user",
      note,
    };

    data.audit.push(auditEntry);
    localStorage.setData(data);
  }

  async listDataTypes(): Promise<{ items: Type[]; total: number }> {
    const apiTypes = await this.req<any[]>("GET", "/datatypes");

    // Transform if needed (your Type output looks correct already)
    return apiTypes.map((apiType: Type) => ({
      key: apiType.key,
      name: apiType.name,
      description: apiType.description,
      displayName: apiType.displayName,
      seq: apiType.seq,
      useFlag: apiType.useFlag,
      parentType: apiType.parentType,
      parentCode: apiType.parentCode,
      createdAt: apiType.createdAt,
      createdBy: apiType.createdBy,
      isDeleted: apiType.isDeleted,
      numericCode: apiType.numericCode,
    }));
  }

  async getType(key: string): Promise<Type | undefined> {
    const data = localStorage.getData();
    return data.types.find((t) => t.key === key && !t.isDeleted);
  }

  async createDataType(insertType: InsertType): Promise<Type> {
    const data = localStorage.getData();

    // Check for duplicate keys
    if (
      data.types.some(
        (t) =>
          t.key.toLowerCase() === insertType.key.toLowerCase() && !t.isDeleted
      )
    ) {
      throw new Error(`Type with key '${insertType.key}' already exists`);
    }

    const type: Type = {
      ...insertType,
      createdAt: new Date().toISOString(),
      createdBy: "current-user",
      isDeleted: false,
    };

    data.types.push(type);
    localStorage.setData(data);

    this.addAuditEntry("CREATE", "TYPE", type.key, undefined, type);

    return type;
  }

  async updateDataType(key: number, updates: Partial<Type>): Promise<Type> {
    const data = localStorage.getData();
    const typeIndex = data.types.findIndex(
      (t) => t.key === key && !t.isDeleted
    );

    if (typeIndex === -1) {
      throw new Error(`Type '${key}' not found`);
    }

    const oldValue = { ...data.types[typeIndex] };
    const updatedType = { ...data.types[typeIndex], ...updates };

    data.types[typeIndex] = updatedType;
    localStorage.setData(data);

    this.addAuditEntry("UPDATE", "TYPE", key, oldValue, updatedType);

    return updatedType;
  }

  async deleteDataType(key: string, force = false): Promise<void> {
    const data = localStorage.getData();
    const typeIndex = data.types.findIndex(
      (t) => t.key === key && !t.isDeleted
    );

    if (typeIndex === -1) {
      throw new Error(`Type '${key}' not found`);
    }

    // Check for dependent codes unless force delete
    if (!force) {
      const dependentCodes = data.codes.filter(
        (c) => c.type === key && !c.isDeleted
      );
      if (dependentCodes.length > 0) {
        throw new Error(
          `Cannot delete type '${key}' because it has ${dependentCodes.length} dependent codes. Use force delete to override.`
        );
      }
    }

    const oldValue = { ...data.types[typeIndex] };
    data.types[typeIndex].isDeleted = true;
    localStorage.setData(data);

    this.addAuditEntry("DELETE", "TYPE", key, oldValue, undefined);
  }

  async listCodes(
    typeFilter?: string,
    parentTypeFilter?: string,
    parentCodeFilter?: string
  ): Promise<Code[]> {
    const data = localStorage.getData();
    let codes = data.codes.filter((c: Code) => !c.isDeleted);

    if (typeFilter) {
      codes = codes.filter((c: Code) => c.type === typeFilter);
    }

    if (parentTypeFilter) {
      codes = codes.filter((c: Code) => c.parentType === parentTypeFilter);
    }

    if (parentCodeFilter) {
      codes = codes.filter((c: Code) => c.parentCode === parentCodeFilter);
    }

    return codes;
  }

  async getCode(type: string, code: string): Promise<Code | undefined> {
    const data = localStorage.getData();
    return data.codes.find(
      (c) => c.type === type && c.code === code && !c.isDeleted
    );
  }

  async createCode(insertCode: InsertCode): Promise<Code> {
    const data = localStorage.getData();

    // Check for duplicate codes within the same type and parent
    const duplicate = data.codes.find(
      (c) =>
        c.type === insertCode.type &&
        c.code.toLowerCase() === insertCode.code.toLowerCase() &&
        c.parentType === insertCode.parentType &&
        c.parentCode === insertCode.parentCode &&
        !c.isDeleted
    );

    if (duplicate) {
      throw new Error(
        `Code '${insertCode.code}' already exists in type '${insertCode.type}'`
      );
    }

    const code: Code = {
      ...insertCode,
      code: data.settings.canonicalizeCode
        ? insertCode.code.toUpperCase()
        : insertCode.code,
      createdAt: new Date().toISOString(),
      createdBy: "current-user",
      isDeleted: false,
    };

    data.codes.push(code);
    localStorage.setData(data);

    this.addAuditEntry(
      "CREATE",
      "CODE",
      `${code.type}:${code.code}`,
      undefined,
      code
    );

    return code;
  }

  async updateAttribute(
    type: string,
    code: string,
    updates: Partial<Code>
  ): Promise<Code> {
    const data = localStorage.getData();
    const codeIndex = data.codes.findIndex(
      (c) => c.type === type && c.code === code && !c.isDeleted
    );

    if (codeIndex === -1) {
      throw new Error(`Code '${code}' not found in type '${type}'`);
    }

    const oldValue = { ...data.codes[codeIndex] };
    const updatedCode = { ...data.codes[codeIndex], ...updates };

    data.codes[codeIndex] = updatedCode;
    localStorage.setData(data);

    this.addAuditEntry(
      "UPDATE",
      "CODE",
      `${type}:${code}`,
      oldValue,
      updatedCode
    );

    return updatedCode;
  }

  async deleteCode(type: string, code: string, force = false): Promise<void> {
    const data = localStorage.getData();
    const codeIndex = data.codes.findIndex(
      (c) => c.type === type && c.code === code && !c.isDeleted
    );

    if (codeIndex === -1) {
      throw new Error(`Code '${code}' not found in type '${type}'`);
    }

    const oldValue = { ...data.codes[codeIndex] };
    data.codes[codeIndex].isDeleted = true;
    localStorage.setData(data);

    this.addAuditEntry(
      "DELETE",
      "CODE",
      `${type}:${code}`,
      oldValue,
      undefined
    );
  }

  async listTypeEdges(): Promise<TypeEdge[]> {
    const data = localStorage.getData();
    return data.typeEdges;
  }

  async createTypeEdge(insertEdge: InsertTypeEdge): Promise<TypeEdge> {
    const data = localStorage.getData();

    // Check for duplicate edge
    const duplicate = data.typeEdges.find(
      (e) =>
        e.fromType === insertEdge.fromType && e.toType === insertEdge.toType
    );

    if (duplicate) {
      throw new Error(
        `Edge from '${insertEdge.fromType}' to '${insertEdge.toType}' already exists`
      );
    }

    const edge: TypeEdge = {
      ...insertEdge,
      createdAt: new Date().toISOString(),
    };

    data.typeEdges.push(edge);
    localStorage.setData(data);

    return edge;
  }

  async deleteTypeEdge(fromType: string, toType: string): Promise<void> {
    const data = localStorage.getData();
    const edgeIndex = data.typeEdges.findIndex(
      (e) => e.fromType === fromType && e.toType === toType
    );

    if (edgeIndex === -1) {
      throw new Error(`Edge from '${fromType}' to '${toType}' not found`);
    }

    data.typeEdges.splice(edgeIndex, 1);
    localStorage.setData(data);
  }

  async search(query: string): Promise<SearchResult[]> {
    const data = localStorage.getData();
    const results: SearchResult[] = [];
    const lowQuery = query.toLowerCase().trim();

    if (!lowQuery) return results;

    // Create search scoring function
    const getMatchScore = (text: string): number => {
      const lowText = text.toLowerCase();

      // Exact match gets highest score
      if (lowText === lowQuery) return 100;

      // Word boundary matches get high score
      const wordBoundaryRegex = new RegExp(
        `\\b${lowQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
        "i"
      );
      if (wordBoundaryRegex.test(text)) return 80;

      // Starts with query gets good score
      if (lowText.startsWith(lowQuery)) return 60;

      // Contains query gets lower score
      if (lowText.includes(lowQuery)) return 40;

      return 0;
    };

    // Search types with scoring
    for (const type of data.types.filter((t) => !t.isDeleted)) {
      const nameScore = getMatchScore(type.name);
      const keyScore = getMatchScore(type.key);
      const descScore = type.description ? getMatchScore(type.description) : 0;

      const maxScore = Math.max(nameScore, keyScore, descScore);

      if (maxScore > 0) {
        results.push({
          type: "TYPE",
          key: type.key,
          name: type.name,
          path: this.buildTypePath(type, data),
          score: maxScore,
        });
      }
    }

    // Search codes with scoring
    for (const code of data.codes.filter((c) => !c.isDeleted)) {
      const nameScore = getMatchScore(code.name);
      const codeScore = getMatchScore(code.code);
      const descScore = code.description ? getMatchScore(code.description) : 0;

      const maxScore = Math.max(nameScore, codeScore, descScore);

      if (maxScore > 0) {
        results.push({
          type: "CODE",
          key: `${code.type}:${code.code}`,
          name: code.name,
          code: code.code,
          path: this.buildCodePath(code, data),
          score: maxScore,
        });
      }
    }

    // Sort by score (highest first) and limit results
    return results.sort((a, b) => (b.score || 0) - (a.score || 0)).slice(0, 50);
  }

  private buildTypePath(type: Type, data: StorageData): string {
    const path = ["TYPES"];
    if (type.parentType && type.parentCode) {
      // Add parent path logic here
    }
    path.push(type.name);
    return path.join(" > ");
  }

  private buildCodePath(code: Code, data: StorageData): string {
    const path = ["TYPES"];

    // Find the type
    const type = data.types.find((t) => t.key === code.type && !t.isDeleted);
    if (type) {
      path.push(type.name);
    }

    path.push(code.name);
    return path.join(" > ");
  }

  async resolve(
    type: string,
    code?: string
  ): Promise<{ path: string; fullPath: string }> {
    const data = localStorage.getData();

    if (code) {
      const codeObj = await this.getCode(type, code);
      if (!codeObj) {
        throw new Error(`Code '${code}' not found in type '${type}'`);
      }

      const path = this.buildCodePath(codeObj, data);
      return {
        path,
        fullPath: `TYPES → ${type} → ${code}`,
      };
    } else {
      const typeObj = await this.getType(type);
      if (!typeObj) {
        throw new Error(`Type '${type}' not found`);
      }

      const path = this.buildTypePath(typeObj, data);
      return {
        path,
        fullPath: `TYPES → ${type}`,
      };
    }
  }

  // async analyzeDataQuality(): Promise<DataQualityIssue[]> {
  //   const data = localStorage.getData();
  //   const issues: DataQualityIssue[] = [];

  //   // Find duplicate names within same type
  //   const nameGroups = new Map<string, Code[]>();
  //   for (const code of data.codes.filter((c) => !c.isDeleted)) {
  //     const key = `${code.type}:${code.name.toLowerCase()}`;
  //     if (!nameGroups.has(key)) {
  //       nameGroups.set(key, []);
  //     }
  //     nameGroups.get(key)!.push(code);
  //   }

  //   for (const [key, codes] of Array.from(nameGroups.entries())) {
  //     if (codes.length > 1) {
  //       const [type] = key.split(":");
  //       issues.push({
  //         id: nanoid(),
  //         type: "DUPLICATE",
  //         severity: "HIGH",
  //         title: "Duplicate Name Found",
  //         description: `Multiple codes with name "${codes[0].name}" in type "${type}"`,
  //         entityType: "CODE",
  //         entityKey: key,
  //         path: this.buildCodePath(codes[0], data),
  //         suggestions: [
  //           "Rename one of the codes",
  //           "Merge the codes if they represent the same entity",
  //         ],
  //       });
  //     }
  //   }

  //   // Find orphaned codes (codes referencing non-existent types)
  //   const typeKeys = new Set(
  //     data.types.filter((t: Type) => !t.isDeleted).map((t: Type) => t.key)
  //   );
  //   for (const code of data.codes.filter((c) => !c.isDeleted)) {
  //     if (!typeKeys.has(code.type)) {
  //       issues.push({
  //         id: nanoid(),
  //         type: "ORPHAN",
  //         severity: "HIGH",
  //         title: "Orphaned Code",
  //         description: `Code "${code.code}" references non-existent type "${code.type}"`,
  //         entityType: "CODE",
  //         entityKey: `${code.type}:${code.code}`,
  //         path: this.buildCodePath(code, data),
  //         suggestions: [
  //           "Create the missing type",
  //           "Move code to existing type",
  //           "Delete the orphaned code",
  //         ],
  //       });
  //     }
  //   }

  //   // Find overlapping names across different types
  //   const crossTypeNameMap = new Map<string, Set<string>>();
  //   for (const code of data.codes.filter((c) => !c.isDeleted)) {
  //     const key = code.name.trim().toLowerCase();
  //     if (!crossTypeNameMap.has(key)) crossTypeNameMap.set(key, new Set());
  //     crossTypeNameMap.get(key)!.add(code.type);
  //   }
  //   for (const [lowName, typeSet] of crossTypeNameMap.entries()) {
  //     if (typeSet.size > 1) {
  //       // Pick a representative code for path display
  //       const example = data.codes.find(
  //         (c) => c.name.toLowerCase() === lowName && !c.isDeleted
  //       )!;
  //       issues.push({
  //         id: nanoid(),
  //         type: "OVERLAP",
  //         severity: "LOW",
  //         title: `Overlapping Name Across Types`,
  //         description: `The name "${example.name
  //           }" appears in multiple types: ${Array.from(typeSet).join(", ")}`,
  //         entityType: "CODE",
  //         entityKey: example.name,
  //         path: this.buildCodePath(example, data),
  //         suggestions: [
  //           "Harmonize naming across types",
  //           "Qualify names to avoid ambiguity",
  //         ],
  //       });
  //     }
  //   }

  //   // Detect cycles in typeEdges graph
  //   const adj = new Map<string, string[]>();
  //   for (const edge of data.typeEdges) {
  //     if (!adj.has(edge.fromType)) adj.set(edge.fromType, []);
  //     adj.get(edge.fromType)!.push(edge.toType);
  //   }
  //   const visited = new Set<string>();
  //   const stack = new Set<string>();
  //   let cycleReported = false;
  //   const pathStack: string[] = [];
  //   const dfs = (node: string): boolean => {
  //     visited.add(node);
  //     stack.add(node);
  //     pathStack.push(node);
  //     for (const nbr of adj.get(node) || []) {
  //       if (!visited.has(nbr)) {
  //         if (dfs(nbr)) return true;
  //       } else if (stack.has(nbr)) {
  //         // Cycle found; build cycle path
  //         const idx = pathStack.indexOf(nbr);
  //         const cyclePath = [...pathStack.slice(idx), nbr].join(" -> ");
  //         if (!cycleReported) {
  //           issues.push({
  //             id: nanoid(),
  //             type: "CYCLE",
  //             severity: "MEDIUM",
  //             title: "Type Edge Cycle Detected",
  //             description: `Detected cycle in type edges: ${cyclePath}`,
  //             entityType: "TYPE",
  //             entityKey: cyclePath,
  //             path: `Edges: ${cyclePath}`,
  //             suggestions: ["Remove one of the edges to break the cycle"],
  //           });
  //         }
  //         cycleReported = true;
  //         return true;
  //       }
  //     }
  //     stack.delete(node);
  //     pathStack.pop();
  //     return false;
  //   };
  //   for (const node of adj.keys()) {
  //     if (!visited.has(node)) {
  //       if (dfs(node)) break;
  //     }
  //   }

  //   return issues;
  // }

  async exportData(scope = "all"): Promise<any[]> {
    const data = localStorage.getData();
    const exportData: any[] = [];

    if (scope === "all" || scope === "types") {
      for (const type of data.types.filter((t: Type) => !t.isDeleted)) {
        exportData.push({
          entityType: "TYPE",
          ...type,
        });
      }
    }

    if (scope === "all" || scope === "codes") {
      for (const code of data.codes.filter((c: Code) => !c.isDeleted)) {
        exportData.push({
          entityType: "CODE",
          ...code,
        });
      }
    }

    return exportData;
  }

  async getAuditLog(filters?: {
    entityType?: string;
    action?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<AuditEntry[]> {
    const data = localStorage.getData();
    let audit = [...data.audit];

    if (filters) {
      if (filters.entityType) {
        audit = audit.filter((a) => a.entityType === filters.entityType);
      }
      if (filters.action) {
        audit = audit.filter((a) => a.action === filters.action);
      }
      if (filters.dateFrom) {
        audit = audit.filter((a) => a.timestamp >= filters.dateFrom!);
      }
      if (filters.dateTo) {
        audit = audit.filter((a) => a.timestamp <= filters.dateTo!);
      }
    }

    return audit.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
  }

  async getStats(): Promise<{
    typesCount: number;
    codesCount: number;
    activeTypesCount: number;
    activeCodesCount: number;
  }> {
    const data = localStorage.getData();
    const types = data.types.filter((t) => !t.isDeleted);
    const codes = data.codes.filter((c) => !c.isDeleted);
    const activeTypes = types.filter((t) => t.useFlag);
    const activeCodes = codes.filter((c) => c.useFlag);

    return {
      typesCount: types.length,
      codesCount: codes.length,
      activeTypesCount: activeTypes.length,
      activeCodesCount: activeCodes.length,
    };
  }

  // AI Assistant
  async chatWithAI(message: string, sessionId: string, microservice?: string, page: number = 1): Promise<{ assistantReply: string; sessionId: string; messages?: any[]; hasMore?: boolean; totalMessages?: number; added?: "datatype" | "attribute"; mainUrl?: { obj: string; val: string }; otherUrl?: { obj: string; val: string }[] }> {
    return {
      assistantReply: "This is a local dummy response.",
      sessionId: sessionId || "local-session-id",
      messages: [],
      hasMore: false,
      totalMessages: 0
    };
  }
}

export class APIDataSource implements ICodeManagerDataSource {

  private isRefreshing = false;
  private refreshSubscribers: Array<(token: string) => void> = [];

  private base(): string {
    return import.meta.env.VITE_AI_BASE_URL_API || "";
  }
  private baseTypes(): string {
    return import.meta.env.VITE_AI_BASE_URL_APITYPES || "";
  }
  private bprmTypes(): string {
    return import.meta.env.VITE_API_BASE_URL_BPRM || "";
  }

  private subscribeTokenRefresh(cb: (token: string) => void) {
    this.refreshSubscribers.push(cb);
  }

  // Notify all subscribers when refresh completes
  private onRefreshed(token: string) {
    this.refreshSubscribers.forEach((cb) => cb(token));
    this.refreshSubscribers = [];
  }


  // Handle logout
  private async handleLogout() {
    try {
      const headers = new Headers();
      headers.set("Content-Type", "application/json");

      const csrfToken = getCookie("csrf_token");
      if (csrfToken) headers.set("X-CSRF-Token", csrfToken);

      await fetch(`${this.bprmTypes()}/auth/logout`, {
        method: "POST",
        headers,
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout failed:", error);
    }
  }

  private async refreshToken(): Promise<boolean> {
    try {
      const headers = new Headers();
      headers.set("Content-Type", "application/json");

      const csrfToken = getCookie("csrf_token");
      if (csrfToken) headers.set("X-CSRF-Token", csrfToken);

      const res = await fetch(`${this.bprmTypes()}/auth/refresh`, {
        method: "POST",
        headers,
        credentials: "include",
      });
      console.log("refreshToken", res)
      if (!res.ok) {
        return false;
      }

      await res.json();
      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    }
  }

  // private async handleAuthError<T>(
  //   retryFn: () => Promise<Response>,
  //   isAuthCall: boolean = false
  // ): Promise<T> {
  //   // If already refreshing, wait for it to complete
  //   if (this.isRefreshing) {
  //     return new Promise((resolve, reject) => {
  //       this.subscribeTokenRefresh(async () => {
  //         try {
  //           const response = await retryFn();
  //           const data = await response.json();
  //           resolve(data);
  //         } catch (error) {
  //           reject(error);
  //         }
  //       });
  //     });
  //   }

  //   // Start refresh process
  //   this.isRefreshing = true;

  //   try {
  //     const refreshSuccess = await this.refreshToken();
  //     console.log("refreshSuccess", refreshSuccess)
  //     if (refreshSuccess) {
  //       // Refresh succeeded, retry the original request
  //       console.log("retryFn", retryFn)
  //       this.onRefreshed("success");
  //       const response = await retryFn();
  //       console.log("retryFn response", response)
  //       if (!response.ok) {
  //         const text = await response.text();
  //         throw new Error(`${response.status}: ${text}`);
  //       }

  //       return response.json();
  //     } else {
  //       // Refresh failed, logout and redirect
  //       await this.handleLogout();
  //       throw new Error("Authentication failed. Redirecting to login...");
  //     }
  //   } finally {
  //     this.isRefreshing = false;
  //   }
  // }

  private async handleAuthError(
    retryFn: () => Promise<Response>
  ): Promise<Response> {
    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.subscribeTokenRefresh(async () => {
          try {
            const response = await retryFn();
            resolve(response);
          } catch (err) {
            reject(err);
          }
        });
      });
    }

    this.isRefreshing = true;

    try {
      const refreshSuccess = await this.refreshToken();

      if (!refreshSuccess) {
        await this.handleLogout();
        throw new Error("Refresh failed");
      }

      this.onRefreshed("success");
      return await retryFn();
    } finally {
      this.isRefreshing = false;
    }
  }

  private async fetchWithAuth(
    baseUrl: string,
    method: string,
    path: string,
    options: {
      headers?: Headers;
      body?: BodyInit;
      isAuthCall?: boolean;
    } = {}
  ): Promise<Response> {
    const makeCall = async () => {
      const headers = options.headers ?? new Headers();

      const csrfToken = getCookie("csrf_token");
      if (csrfToken) headers.set("X-CSRF-Token", csrfToken);

      const appId = getCookieAsItIS("app_id") || "1";
      if (appId) headers.set("X-App-Id", appId);

      return fetch(`${baseUrl}${path}`, {
        method,
        headers,
        body: options.body,
        credentials: "include",
      });
    };

    const res = await makeCall();

    if (res.status === 401 || res.status === 403) {
      return this.handleAuthError(makeCall);
    }

    return res;
  }

  private async makeRequest<T>(
    baseUrl: string,
    method: string,
    path: string,
    body?: any,
    isAuthCall: boolean = false
  ): Promise<T> {
    const headers = new Headers();
    if (body) headers.set("Content-Type", "application/json");

    const res = await this.fetchWithAuth(baseUrl, method, path, {
      headers,
      body: body ? JSON.stringify(body) : undefined,
      isAuthCall,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status}: ${text}`);
    }

    return res.json();
  }

  private async makeRequestBlob(
    baseUrl: string,
    method: string,
    path: string,
    isAuthCall: boolean = false
  ): Promise<Blob> {
    const res = await this.fetchWithAuth(baseUrl, method, path, {
      isAuthCall,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status}: ${text}`);
    }

    return res.blob();
  }

  private async makeRequestFormData<T>(
    baseUrl: string,
    method: string,
    path: string,
    formData: FormData,
    isAuthCall: boolean = false
  ): Promise<T> {
    const headers = new Headers();

    const res = await this.fetchWithAuth(baseUrl, method, path, {
      headers,
      body: formData,
      isAuthCall,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`${res.status}: ${text}`);
    }

    return res.json();
  }




  async req<T>(method: string, path: string, body?: any): Promise<T> {
    return this.makeRequest<T>(this.base(), method, path, body);
  }

  async reqTypes<T>(method: string, path: string, body?: any): Promise<T> {
    return this.makeRequest<T>(this.baseTypes(), method, path, body);
  }

  async reqBprmTypes<T>(method: string, path: string, body?: any): Promise<T> {
    return this.makeRequest<T>(this.bprmTypes(), method, path, body, true);
  }

  protected async requestRaw<T>(
    baseUrl: string,
    method: string,
    path: string,
    body?: any,
    isAuthCall: boolean = false
  ): Promise<T> {
    return this.makeRequest<T>(baseUrl, method, path, body, isAuthCall);
  }

  // Authentication
  async authMe(): Promise<AuthSuccess | AuthError> {
    try {
      const result = await this.reqBprmTypes<AuthSuccess | AuthError>("GET", `/auth/me`);
      return result;
    } catch (error) {
      console.log("authMe() error:", error);
      throw error;
    }
  }

  async refresh(): Promise<RefreshSuccess | AuthError> {
    return this.reqBprmTypes<RefreshSuccess | AuthError>("POST", `/auth/refresh`);
  }

  async logout(): Promise<LogoutSuccess | AuthError> {
    try {
      return await this.reqBprmTypes<LogoutSuccess | AuthError>("POST", `/auth/logout`);
    } catch (error) {
      throw error;
    }
  }


  async listTypes(parentType: String, parentCode: number): Promise<any> {
    return this.reqTypes<Type>("GET", `/types?parentType=${parentType}&parentCode=${parentCode}&includeDeleted=false&includeRoot=false`);
  }

  async listAllTypes(): Promise<any> {
    return this.reqTypes<any>("GET", `/types/all?includeDeleted=false`);
  }

  // DataTypes
  async listDataTypes(pageIndex: number, pageSize?: number, typeKey?: string): Promise<{ items: Type[]; total: number }> {

    if (!pageSize) {
      return this.req<{ items: Type[]; total: number }>("GET", `/datatypes?order=newest&page=${pageIndex}`);
    }

    if (typeKey && typeKey !== "ALL") {
      return this.req<{ items: Type[]; total: number }>("GET", `/datatypes?name=${typeKey}&order=newest&page=${pageIndex}&pageSize=${pageSize}`);
    }
    return this.req<{ items: Type[]; total: number }>("GET", `/datatypes?order=newest&page=${pageIndex}&pageSize=${pageSize}`);

  }

  async listAttributes(parentCode: string): Promise<Code[]> {

    const attributeData = await this.req<{ datatypeId: number, typeName: string, count: number, items: Code[] }>("GET", `/datatypes/${parentCode}/attributes`);
    return attributeData.items.map((item) => {
      if (
        typeof item.DropdownKind === "string"
      ) {
        if (item.DropdownKind.includes("@")) {
          const [childAttribute, dropdownKind] = item.DropdownKind.split("@");

          return {
            ...item,
            DropdownKind: dropdownKind,
            childAttribute,
          };
        } else {
          return {
            ...item,
            childAttribute: "",
          };

        }

      }

      return {
        ...item,
      };
    });
  }

  async filterCatalogDataTypes(
    name?: string,
    page: number = 1,
    pageSize: number = 10,
    sort: DataTypeSortOption = "Standard"
  ): Promise<{ items: Type[]; total: number; page: number; pageSize: number; page_size?: number; sort?: string }> {
    const params = new URLSearchParams();
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    params.set("pageSize", String(pageSize));

    if (name && name.trim()) {
      params.set("name", name.trim());
      params.set("term", name.trim());
    }

    let sortBy = "id";
    let sortDir = "desc";
    if (sort === "Lex") {
      sortBy = "name";
      sortDir = "asc";
    } else if (sort === "Id_based") {
      sortBy = "id";
      sortDir = "desc";
    } else {
      sortBy = "id";
      sortDir = "desc";
    }

    params.set("sort", sort || "Standard");
    params.set("sort_by", sortBy);
    params.set("sort_dir", sortDir);
    params.set("sortBy", sortBy);
    params.set("sortDir", sortDir);

    const qs = params.toString();
    try {
      const data = await this.req<any>("GET", `/api/datatypes/name/search?${qs}`);
      return this.normalizeFilterResponse(data, page, pageSize, sort);
    } catch (err: any) {
      try {
        const data = await this.req<any>("GET", `/datatypes/name/search?${qs}`);
        return this.normalizeFilterResponse(data, page, pageSize, sort);
      } catch (fallbackErr: any) {
        throw err;
      }
    }
  }

  private normalizeFilterResponse(
    data: any,
    page: number,
    pageSize: number,
    sort: string
  ): { items: Type[]; total: number; page: number; pageSize: number; page_size?: number; sort?: string } {
    if (!data) return { items: [], total: 0, page, pageSize, sort };
    if (Array.isArray(data)) {
      return {
        items: data,
        total: data.length,
        page,
        pageSize,
        page_size: pageSize,
        sort,
      };
    }
    const items = data.items || data.data || [];
    const total = data.total ?? data.totalCount ?? data.total_count ?? items.length;
    return {
      items,
      total,
      page: data.page ?? page,
      pageSize: data.pageSize ?? data.page_size ?? pageSize,
      page_size: data.page_size ?? data.pageSize ?? pageSize,
      sort: data.sort || sort,
    };
  }

  async listFilterType(filterName?: string, filterType?: string): Promise<Type[]> {

    return this.req<Type[]>("GET", `/catalog/filter?name=${filterName}&type=${filterType}&includeDeleted=false`);
  }

  async getType(key: string): Promise<Type | undefined> {
    return this.req<Type>("GET", `/types/${encodeURIComponent(key)}`);
  }

  async createDataType(type: InsertType): Promise<Type> {
    return this.req<Type>("POST", "/datatypes", type);
  }

  async updateDataType(key: number, updates: Partial<Type>): Promise<Type> {
    console.log("key", key)
    return this.req<Type>("PUT", `/datatypes/${encodeURIComponent(key)}`, updates);
  }

  async deleteDataType(key: string, force?: boolean): Promise<void> {
    await this.req<void>(
      "DELETE",
      `/datatypes/${key}`
    );
  }

  async listCodes(
    typeFilter?: string,
    parentTypeFilter?: string,
    parentCodeFilter?: string
  ): Promise<Code[]> {
    const params = new URLSearchParams();
    if (typeFilter) params.set("type", typeFilter);
    if (parentTypeFilter) params.set("parentType", parentTypeFilter);
    if (parentCodeFilter) params.set("parentCode", parentCodeFilter);
    const qs = params.toString();

    const apiCodes = await this.req<any[]>(
      "GET",
      `/codes${qs ? `?${qs}` : ""}`
    );

    // Transform API response to match frontend schema
    return apiCodes.map((apiCode) => ({
      type: apiCode.Type || "",
      code: apiCode.Code || "",
      name: apiCode.Name || "",
      description: apiCode.Description || undefined,
      seq: apiCode.Seq || 0,
      useFlag: apiCode.UseFlag || true,
      parentType: apiCode.ParentType || undefined,
      parentCode: apiCode.ParentCode || undefined,
      createdAt: apiCode.AddedDateTime || new Date().toISOString(),
      createdBy: apiCode.AddedBy || "unknown",
      isDeleted: false,
      numericCode: apiCode.ID?.toString() || "",
    }));
  }

  async getCode(type: string, code: string): Promise<Code | undefined> {
    try {
      const apiCode = await this.req<any>(
        "GET",
        `/codes/${encodeURIComponent(type)}/${encodeURIComponent(code)}`
      );

      return {
        type: apiCode.Type || type,
        code: apiCode.Code || code,
        name: apiCode.Name || "",
        description: apiCode.Description || undefined,
        seq: apiCode.Seq || 0,
        useFlag: apiCode.UseFlag || true,
        parentType: apiCode.ParentType || undefined,
        parentCode: apiCode.ParentCode || undefined,
        createdAt: apiCode.AddedDateTime || new Date().toISOString(),
        createdBy: apiCode.AddedBy || "unknown",
        isDeleted: false,
        numericCode: apiCode.ID?.toString() || "",
      };
    } catch (error) {
      return undefined;
    }
  }

  async createAttribute(Id: string, data: Partial<Code>): Promise<Code> {
    const payload = {
      item: { ...data }
    }
    console.log("data", data)
    console.log("payload", payload)
    return this.req<Code>("POST", `/datatypes/${Id}/attributes`, payload);
  }

  async updateAttribute(
    type: string,
    code: string,
    updates: Partial<Code>
  ): Promise<Code> {
    return this.req<Code>(
      "PATCH",
      `/datatypes/${code}/attributes/${type}`,
      updates
    );
  }

  async deleteAttribute(type: string, code: string, force?: boolean): Promise<void> {
    await this.req<void>(
      "DELETE",
      `/datatypes/${type}/attributes/${code}`
    );
  }

  // Type edges
  async listTypeEdges(): Promise<TypeEdge[]> {
    return this.req<TypeEdge[]>("GET", "/type-edges");
  }
  async createTypeEdge(edge: InsertTypeEdge): Promise<TypeEdge> {
    return this.req<TypeEdge>("POST", "/type-edges", edge);
  }
  async deleteTypeEdge(fromType: string, toType: string): Promise<void> {
    await this.req<void>(
      "DELETE",
      `/type-edges/${encodeURIComponent(fromType)}/${encodeURIComponent(
        toType
      )}`
    );
  }

  // Search & resolve
  async search(query: string) {
    const qs = new URLSearchParams({ q: query });
    return this.req<any[]>("GET", `/search?${qs.toString()}`);
  }
  async resolve(type: string, code?: string) {
    const qs = new URLSearchParams({ type, ...(code ? { code } : {}) });
    return this.req<{ path: string; fullPath: string }>(
      "GET",
      `/resolve?${qs.toString()}`
    );
  }

  // Data quality (server)
  async analyzeDataQuality() {
    return this.req<any[]>("GET", "/data-quality");
  }

  // async exportData(
  //   dataset: "all" | "types" | "codes" = "all",
  //   format: "csv" | "json" = "csv"
  // ): Promise<Blob> {
  //   const params = new URLSearchParams({ dataset, format });

  //   const response = await fetch(`${this.base()}/transfer/export?${params.toString()}`, {
  //     method: "GET",
  //     credentials: "include",
  //   });

  //   if (!response.ok) {
  //     throw new Error(`Export failed: ${response.status} ${response.statusText}`);
  //   }

  //   const blob = await response.blob();

  //   console.log("Content-Type:", blob.type); // debug
  //   return blob; // just return the blob, handle filename in client
  // }
  async exportData(
    table_type: "all" | "datatypes" | "attributes" = "all",
    format: "csv" | "json" = "csv"
  ): Promise<Blob> {
    const params = new URLSearchParams({ table_type, format });

    return this.makeRequestBlob(
      this.base(),
      "GET",
      `/transfer/export?${params.toString()}`
    );
  }



  // async importData(
  //   file: File,
  //   format: "csv" | "json" = "json",
  //   dryRun: boolean = true,
  //   validateReferences: boolean = true,
  //   checkDuplicates: boolean = true,
  //   mode: "insert" | "update" | "upsert" = "insert",
  //   dataset: "all" | "types" | "codes" = "all"
  // ): Promise<ImportResult> {
  //   const formData = new FormData();
  //   formData.append("file", file);

  //   const params = new URLSearchParams({
  //     mode,
  //     dataset,
  //     dryRun: dryRun.toString(),
  //     // format,
  //     // validateReferences: validateReferences.toString(),
  //     // checkDuplicates: checkDuplicates.toString()
  //   });

  //   const response = await fetch(`${this.base()}/transfer/import?${params.toString()}`, {
  //     method: 'POST',
  //     body: formData,
  //     credentials: 'include',
  //   });

  //   if (!response.ok) {
  //     throw new Error(`Import failed: ${response.status} ${response.statusText}`);
  //   }

  //   // return await response.json();
  //   const apiResponse = await response.json();

  //   // 🔄 Normalize response for frontend
  //   const processedRows =
  //     (apiResponse.summary?.codes?.created ?? 0) +
  //     (apiResponse.summary?.codes?.updated ?? 0) +
  //     (apiResponse.summary?.codes?.skipped ?? 0) +
  //     (apiResponse.summary?.types?.created ?? 0) +
  //     (apiResponse.summary?.types?.updated ?? 0) +
  //     (apiResponse.summary?.types?.skipped ?? 0);

  //   return {
  //     success: apiResponse.ok,
  //     processedRows,
  //     errors: apiResponse.errors ?? [],
  //     warnings: apiResponse.warnings ?? [], // might not exist in API
  //     data: apiResponse.summary ?? {},
  //   };
  // }

  // Audit
  async importData(
    file: File,
    format: "csv" | "json" = "json",
    dryRun: boolean = true,
    validateReferences: boolean = true,
    checkDuplicates: boolean = true,
    mode: "insert" | "update" | "upsert" = "insert",
    dataset: "all" | "datatypes" | "attributes" = "all"
  ): Promise<ImportResult> {
    const formData = new FormData();
    formData.append("file", file);

    const params = new URLSearchParams({
      mode,
      dataset,
      dryRun: dryRun.toString(),
    });

    const apiResponse = await this.makeRequestFormData<any>(
      this.base(),
      "POST",
      `/transfer/import?${params.toString()}`,
      formData
    );

    const processedRows =
      (apiResponse.summary?.Datatypes?.created ?? 0) +
      (apiResponse.summary?.Datatypes?.updated ?? 0) +
      (apiResponse.summary?.Datatypes?.skipped ?? 0) +
      (apiResponse.summary?.Datatypes?.created ?? 0) +
      (apiResponse.summary?.Datatypes?.updated ?? 0) +
      (apiResponse.summary?.Datatypes?.skipped ?? 0);

    return {
      success: apiResponse.ok,
      processedRows,
      errors: apiResponse.errors ?? [],
      warnings: apiResponse.warnings ?? [],
      data: apiResponse.summary ?? {},
    };
  }

  async getAuditLog(filters?: {
    errorCode?: string;
    statusCode?: string;
    dateFrom?: string;
    dateTo?: string;
    pageSize?: string;
    pageIndex?: string;
  }): Promise<AuditData[]> {

    const params = new URLSearchParams();

    // Pagination (always included)
    params.set("limit", filters?.pageSize ?? "10");
    params.set("offset", filters?.pageIndex ?? "1");

    // Optional filters (only if provided)
    if (filters?.errorCode) {
      params.set("error_type", filters.errorCode);
    }

    if (filters?.statusCode) {
      params.set("status_code", filters.statusCode);
    }

    if (filters?.dateFrom) {
      params.set("start_date", filters.dateFrom);
    }

    if (filters?.dateTo) {
      params.set("end_date", filters.dateTo);
    }

    return this.req<AuditData[]>(
      "GET",
      `/audit?${params.toString()}`
    );
  }

  // Stats
  async getStats(): Promise<{
    typesCount: number;
    codesCount: number;
    activeTypesCount: number;
    activeCodesCount: number;
  }> {
    return this.req("GET", "/stats");
  }

  // In your APIDataSource class
  // In your APIDataSource class (lib/dataSource.ts)
  async getSettings(): Promise<any> {
    try {
      return await this.req("GET", "/settings");
    } catch (error) {
      console.warn("Settings API not available, using defaults");
      // Return default settings if API fails
      return {
        canonicalizeCode: true,
        enforceUniqueNames: true,
        nlpProvider: "LocalRules",
        autoSave: true,
        theme: "light",
      };
    }
  }

  async updateSettings(settings: any): Promise<any> {
    try {
      return await this.req("PUT", "/settings", settings);
    } catch (error) {
      console.warn("Settings API not available, saving locally");
      // Save to localStorage as fallback
      localStorage.setItem("app-settings", JSON.stringify(settings));
      return settings;
    }
  }

  // AI Assistant
  async chatWithAI(message: string, sessionId: string, microservice: string = "datamanager", page: number = 1): Promise<{ assistantReply: string; sessionId: string; messages?: any[]; hasMore?: boolean; totalMessages?: number; added?: "datatype" | "attribute"; mainUrl?: { obj: string; val: string }; otherUrl?: { obj: string; val: string }[] }> {
    const payload: any = { microservice };
    if (message) payload.message = message;
    if (sessionId) payload.sessionId = sessionId;

    return this.makeRequest<{ assistantReply: string; sessionId: string; messages?: any[]; hasMore?: boolean; totalMessages?: number; added?: "datatype" | "attribute"; mainUrl?: { obj: string; val: string }; otherUrl?: { obj: string; val: string }[] }>(
      this.bprmTypes(),
      "POST",
      `/assistant/chat?page=${page}`,
      payload
    );
  }
}

const apiDS = new APIDataSource();
export const dataSource: ICodeManagerDataSource = apiDS;
